import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Building2,
  Search,
  RefreshCcw,
  Printer,
} from "lucide-react";

const BASE_URL = "http://nissi-india.com"; // change if needed

const MONTHS = [
  { value: "Select", label: "Select" },
  { value: "January", label: "January" },
  { value: "February", label: "February" },
  { value: "March", label: "March" },
  { value: "April", label: "April" },
  { value: "May", label: "May" },
  { value: "June", label: "June" },
  { value: "July", label: "July" },
  { value: "August", label: "August" },
  { value: "September", label: "September" },
  { value: "October", label: "October" },
  { value: "November", label: "November" },
  { value: "December", label: "December" },
];

function buildYears() {
  const nowY = new Date().getFullYear();
  const ys = ["Select"];
  for (let y = 2000; y <= nowY; y++) ys.push(String(y));
  return ys;
}

const SalaryStatement = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const loginBranchId = localStorage.getItem("branchId") || "";      // you likely store this already
  const branchType = localStorage.getItem("branchType") || "HO";       // "HO" etc

  // meta
  const [branches, setBranches] = useState([]);
  const [metaLoading, setMetaLoading] = useState(false);

  // filters
  const [branchId, setBranchId] = useState("Select");
  const [monthName, setMonthName] = useState("Select");
  const [year, setYear] = useState("Select");
  const [isPF, setIsPF] = useState("Yes"); // ASP default Yes

  // cash grid
  const [cashRows, setCashRows] = useState([]);
  const [cashLoading, setCashLoading] = useState(false);
  const [cashPage, setCashPage] = useState(1);
  const [cashPageSize] = useState(10);
  const [cashTotal, setCashTotal] = useState(0);
  const [cashTotalPages, setCashTotalPages] = useState(1);

  // bank grid
  const [bankRows, setBankRows] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankPage, setBankPage] = useState(1);
  const [bankPageSize] = useState(10);
  const [bankTotal, setBankTotal] = useState(0);
  const [bankTotalPages, setBankTotalPages] = useState(1);

  const years = useMemo(() => buildYears(), []);
  const screenBadge = useMemo(() => "INFRA SCREEN : C-40", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const disabledBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    "bg-gray-400 text-white cursor-not-allowed";

  const canSearch = () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return false;
    }
    if (!String(loginBranchId).trim()) {
      toast.warn("Login BranchId missing in localStorage (branchId).");
      return false;
    }
    if (!monthName || monthName === "Select") {
      toast.warn("Please select Month.");
      return false;
    }
    if (!year || year === "Select") {
      toast.warn("Please select Year.");
      return false;
    }
    if (isPF !== "Yes" && isPF !== "No") {
      toast.warn("Please Select PF Option");
      return false;
    }
    return true;
  };

  const fetchMeta = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(loginBranchId).trim() || !String(branchType).trim()) {
      toast.warn("Missing branchId/branchType in localStorage.");
      return;
    }

    try {
      setMetaLoading(true);
      const res = await axios.get(`${BASE_URL}/api/salary-statement/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(loginBranchId),
          branchType: String(branchType),
        },
      });

      if (res?.data?.success) {
        const bs = res.data.branches || [];
        setBranches(bs);

        // Defaults like ASP
        const defaultBranchId = String(res.data.defaultBranchId || loginBranchId || "Select");
        setBranchId(defaultBranchId);

        const defaultMonthNo = Number(res.data.defaultMonthNo || new Date().getMonth() + 1);
        const monthObj = MONTHS.find((m) => m.value !== "Select" && MONTHS.indexOf(m) === defaultMonthNo);
        // Above is tricky; safer: map by index: January=1 etc
        const monthNameDefault =
          MONTHS.find((m) => m.value !== "Select" && m.value === MONTHS[defaultMonthNo]?.value)?.value ||
          MONTHS[defaultMonthNo]?.value ||
          "Select";

        setMonthName(monthNameDefault);
        setYear(String(res.data.defaultYear || new Date().getFullYear()));
        setIsPF(res.data.defaultIsPF || "Yes");

        // Initial load: same as ASP (cash+bank)
        setCashPage(1);
        setBankPage(1);
        await Promise.all([fetchCash(1, defaultBranchId, monthNameDefault, String(res.data.defaultYear), res.data.defaultIsPF || "Yes"),
                           fetchBank(1, defaultBranchId, monthNameDefault, String(res.data.defaultYear), res.data.defaultIsPF || "Yes")]);
      } else {
        toast.error(res?.data?.message || "Failed to load meta.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setMetaLoading(false);
    }
  };

  const fetchCash = async (p = cashPage, b = branchId, m = monthName, y = year, pf = isPF) => {
    try {
      setCashLoading(true);
      const res = await axios.get(`${BASE_URL}/api/salary-statement/data`, {
        params: {
          finYearId: Number(finYearId),
          loginBranchId: Number(loginBranchId),
          branchId: b || "Select",
          monthName: m,
          year: y,
          isPF: pf,
          payType: "cash",
          page: p,
          pageSize: cashPageSize,
        },
      });

      if (res?.data?.success) {
        setCashRows(res.data.rows || []);
        setCashTotal(res.data.total || 0);
        setCashTotalPages(res.data.totalPages || 1);
        setCashPage(res.data.page || p);
      } else {
        toast.error(res?.data?.message || "Failed to load Cash list.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setCashLoading(false);
    }
  };

  const fetchBank = async (p = bankPage, b = branchId, m = monthName, y = year, pf = isPF) => {
    try {
      setBankLoading(true);
      const res = await axios.get(`${BASE_URL}/api/salary-statement/data`, {
        params: {
          finYearId: Number(finYearId),
          loginBranchId: Number(loginBranchId),
          branchId: b || "Select",
          monthName: m,
          year: y,
          isPF: pf,
          payType: "bank",
          page: p,
          pageSize: bankPageSize,
        },
      });

      if (res?.data?.success) {
        setBankRows(res.data.rows || []);
        setBankTotal(res.data.total || 0);
        setBankTotalPages(res.data.totalPages || 1);
        setBankPage(res.data.page || p);
      } else {
        toast.error(res?.data?.message || "Failed to load Bank list.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setBankLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    if (!canSearch()) return;

    setCashPage(1);
    setBankPage(1);

    await Promise.all([fetchCash(1), fetchBank(1)]);
  };

  const handleRefresh = async () => {
    if (!canSearch()) return;
    await Promise.all([fetchCash(cashPage), fetchBank(bankPage)]);
  };

  const handlePrint = () => {
    if (!canSearch()) return;

    // Print page route (React Router path you set)
    const qs = new URLSearchParams({
      finYearId: String(finYearId),
      loginBranchId: String(loginBranchId),
      branchId: String(branchId || "Select"),
      monthName: String(monthName),
      year: String(year),
      isPF: String(isPF),
    }).toString();

    // Update this path to your router mapping
    window.open(`/PayRoll/SalaryStatementReport_Print?${qs}`, "_blank", "width=1000,height=900,scrollbars=yes");
  };

  useEffect(() => {
    fetchMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // independent pagination (cash)
  useEffect(() => {
    if (!canSearch()) return;
    fetchCash(cashPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cashPage]);

  // independent pagination (bank)
  useEffect(() => {
    if (!canSearch()) return;
    fetchBank(bankPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankPage]);

  const cashShowingFrom = cashTotal === 0 ? 0 : (cashPage - 1) * cashPageSize + 1;
  const cashShowingTo = Math.min(cashPage * cashPageSize, cashTotal);

  const bankShowingFrom = bankTotal === 0 ? 0 : (bankPage - 1) * bankPageSize + 1;
  const bankShowingTo = Math.min(bankPage * bankPageSize, bankTotal);

  const renderPager = ({ page, totalPages, onPrev, onNext, onSet }) => {
    if (totalPages <= 1) return null;

    return (
      <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
        <button
          onClick={onPrev}
          disabled={page === 1}
          className={`px-4 py-2 text-sm font-bold ${
            page === 1
              ? "text-gray-400 cursor-not-allowed"
              : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
          }`}
        >
          Prev
        </button>

        {[...Array(totalPages)].map((_, i) => (
          <button
            key={i}
            onClick={() => onSet(i + 1)}
            className={`px-4 py-2 text-sm font-bold transition ${
              page === i + 1
                ? `${primaryBtnClass} text-white`
                : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
            }`}
          >
            {i + 1}
          </button>
        ))}

        <button
          onClick={onNext}
          disabled={page === totalPages}
          className={`px-4 py-2 text-sm font-bold ${
            page === totalPages
              ? "text-gray-400 cursor-not-allowed"
              : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
          }`}
        >
          Next
        </button>
      </div>
    );
  };

  const TableSkeleton = ({ cols = 8, rows = 8 }) => (
    <tbody>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r} className={isDark ? "bg-gray-900" : "bg-white"}>
          {Array.from({ length: cols }).map((__, c) => (
            <td key={c} className="px-4 py-4">
              <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );

  const renderStatementTable = (rows, loading, page, pageSize) => (
    <div className={cardBase}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className={`${primaryBtnClass} text-white`}>
            <tr>
              <th className="px-4 py-4 text-center font-bold whitespace-nowrap">S.No</th>
              <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Employee Code</th>
              <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Employee Name</th>
              <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Designation</th>
              <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Account No</th>
              <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Gross Salary</th>
              <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Basic+DA</th>
              <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Leave</th>
              <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Paid Basic+DA</th>
              <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Conveyance Allowance</th>
              <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Educational Allowance</th>
              <th className="px-4 py-4 text-center font-bold whitespace-nowrap">H.R.A</th>
              <th className="px-4 py-4 text-center font-bold whitespace-nowrap">P.F</th>
              <th className="px-4 py-4 text-center font-bold whitespace-nowrap">ESI</th>
              <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Total Advance</th>
              <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Advance Balance</th>
              <th className="px-4 py-4 text-center font-bold whitespace-nowrap">NetSalary</th>
            </tr>
          </thead>

          {loading ? (
            <TableSkeleton cols={17} rows={pageSize} />
          ) : (
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={17}
                    className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}
                  >
                    No Records Found
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => (
                  <tr
                    key={`${r.fld_employeecode}-${idx}`}
                    className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${
                      isDark ? "bg-gray-800" : "bg-gray-50"
                    }`}
                  >
                    <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                      {(page - 1) * pageSize + idx + 1}
                    </td>

                    <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                      {r.fld_employeecode}
                    </td>
                    <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                      {r.fld_employee}
                    </td>
                    <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                      {r.fld_designationname}
                    </td>
                    <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                      {r.fld_accountno}
                    </td>

                    <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                      {r.fld_grosssalary}
                    </td>
                    <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                      {r.fld_basicpay}
                    </td>
                    <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                      {r.fld_lopamt}
                    </td>

                    <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                      {r.fld_paidbasic}
                    </td>

                    <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                      {r.fld_conveyance}
                    </td>
                    <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                      {r.fld_educational}
                    </td>
                    <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                      {r.fld_hra}
                    </td>
                    <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                      {r.fld_pf}
                    </td>

                    <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                      {r.fld_esi}
                    </td>
                    <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                      {r.advance}
                    </td>
                    <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                      {r.advbal}
                    </td>

                    <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                      {r.fld_netpay}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          )}
        </table>
      </div>
    </div>
  );

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SALARY STATEMENT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Salary Statement Report
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Filter → Search → Cash & Bank List with Pagination
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                Cash: {cashTotal} | Bank: {bankTotal}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button
                onClick={handleRefresh}
                className={(!monthName || monthName === "Select" || !year || year === "Select") ? disabledBtn : ghostBtn}
                disabled={cashLoading || bankLoading || metaLoading}
              >
                <RefreshCcw className={`w-4 h-4 ${(cashLoading || bankLoading) ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handlePrint} className={primaryBtn} disabled={cashLoading || bankLoading || metaLoading}>
                <Printer className="w-4 h-4" />
                Print
              </button>
            </div>
          </div>

          {/* Filters */}
          <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className={labelBase}>Branch</label>
                <select
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                  className={`${inputBase} mt-2`}
                  disabled={metaLoading}
                >
                  <option value="Select">Select</option>
                  {branches.map((b) => (
                    <option key={b.fld_branchid} value={String(b.fld_branchid)}>
                      {b.fld_branchname}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelBase}>PF</label>
                <div className="mt-3 flex items-center gap-6">
                  <label className={`flex items-center gap-2 ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                    <input
                      type="radio"
                      name="pf"
                      checked={isPF === "Yes"}
                      onChange={() => setIsPF("Yes")}
                    />
                    Yes
                  </label>
                  <label className={`flex items-center gap-2 ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                    <input
                      type="radio"
                      name="pf"
                      checked={isPF === "No"}
                      onChange={() => setIsPF("No")}
                    />
                    No
                  </label>
                </div>
              </div>

              <div>
                <label className={labelBase}>Month</label>
                <select value={monthName} onChange={(e) => setMonthName(e.target.value)} className={`${inputBase} mt-2`}>
                  {MONTHS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelBase}>Year</label>
                <select value={year} onChange={(e) => setYear(e.target.value)} className={`${inputBase} mt-2`}>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-center mt-6">
              <button type="submit" className={primaryBtn} disabled={cashLoading || bankLoading || metaLoading}>
                <Search className="w-4 h-4" />
                Search
              </button>
            </div>
          </form>

          {/* CASH LIST */}
          <div className="mb-4">
            <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"} mb-3`}>
              Cash List
            </div>

            {renderStatementTable(cashRows, cashLoading, cashPage, cashPageSize)}

            <div className="flex flex-col md:flex-row items-center justify-between mt-4 gap-4">
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Showing <b>{cashShowingFrom}</b> to <b>{cashShowingTo}</b> of <b>{cashTotal}</b>
              </div>

              {renderPager({
                page: cashPage,
                totalPages: cashTotalPages,
                onPrev: () => setCashPage((p) => Math.max(1, p - 1)),
                onNext: () => setCashPage((p) => Math.min(cashTotalPages, p + 1)),
                onSet: (p) => setCashPage(p),
              })}
            </div>
          </div>

          {/* BANK LIST */}
          <div className="mt-10">
            <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"} mb-3`}>
              Bank List
            </div>

            {renderStatementTable(bankRows, bankLoading, bankPage, bankPageSize)}

            <div className="flex flex-col md:flex-row items-center justify-between mt-4 gap-4">
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Showing <b>{bankShowingFrom}</b> to <b>{bankShowingTo}</b> of <b>{bankTotal}</b>
              </div>

              {renderPager({
                page: bankPage,
                totalPages: bankTotalPages,
                onPrev: () => setBankPage((p) => Math.max(1, p - 1)),
                onNext: () => setBankPage((p) => Math.min(bankTotalPages, p + 1)),
                onSet: (p) => setBankPage(p),
              })}
            </div>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default SalaryStatement;